
// Define Patient interface directly in this file since it's not exported from types/cancer.ts
interface Patient {
  id: string;
  name: string;
  dateOfBirth: string;
  gender: string;
  contactNumber: string;
  address: string;
  medicalHistory: string;
  cancerDiagnosisId: string;
}

// Mock data for cancer diagnoses with correct status values and property names
export const mockCancerDiagnoses = [
  {
    id: "cd1",
    patientId: "patient1",
    diagnosisName: "Breast Cancer",
    leadStaffId: "1", // Admin user with id "1"
    involvedStaffIds: ["2", "3"], // Regular user and John Doe
    patientJourneyReview: "Chemotherapy and surgery planned",
    learningPoints: "Early detection critical. Regular follow-ups needed.",
    status: "reviewing",
    createdAt: "2023-01-15T14:32:00Z",
    updatedAt: "2023-01-15T14:32:00Z",
    createdById: "1"
  },
  {
    id: "cd2",
    patientId: "patient2",
    diagnosisName: "Lung Cancer",
    leadStaffId: "4", // Jane Smith
    involvedStaffIds: ["5", "6"], // Michael Johnson and Dr. Sarah
    patientJourneyReview: "Immunotherapy to be started",
    learningPoints: "CT scan effectiveness. Early symptoms identification.",
    status: "readyForMDT",
    createdAt: "2022-11-20T09:15:00Z",
    updatedAt: "2022-11-20T09:15:00Z",
    createdById: "4"
  },
  {
    id: "cd3",
    patientId: "patient3",
    diagnosisName: "Prostate Cancer",
    leadStaffId: "7", // Dr. James Wilson
    involvedStaffIds: ["8", "9"], // Emily Parker and Robert Chen
    patientJourneyReview: "Active surveillance recommended",
    learningPoints: "PSA testing frequency. Diet recommendations.",
    status: "discussed",
    createdAt: "2023-03-01T10:45:00Z",
    updatedAt: "2023-03-01T10:45:00Z",
    createdById: "7"
  },
  {
    id: "cd4",
    patientId: "patient4",
    diagnosisName: "Melanoma",
    leadStaffId: "2", // Regular user
    involvedStaffIds: ["4", "5"], // Jane Smith and Michael Johnson
    patientJourneyReview: "Surgical excision and adjuvant therapy planned",
    learningPoints: "Sun protection education. Regular skin checks.",
    status: "reviewing",
    createdAt: "2022-12-10T16:20:00Z",
    updatedAt: "2022-12-10T16:20:00Z",
    createdById: "2"
  },
  {
    id: "cd5",
    patientId: "patient5",
    diagnosisName: "Colon Cancer",
    leadStaffId: "8", // Emily Parker
    involvedStaffIds: ["6", "7"], // Dr. Sarah and Dr. James
    patientJourneyReview: "Surgery and chemotherapy recommended",
    learningPoints: "Colonoscopy preparation. Post-surgery care.",
    status: "readyForMDT",
    createdAt: "2023-02-28T11:15:00Z",
    updatedAt: "2023-02-28T11:15:00Z",
    createdById: "8"
  },
  {
    id: "cd6",
    patientId: "patient6",
    diagnosisName: "Leukemia",
    leadStaffId: "9", // Robert Chen
    involvedStaffIds: ["1", "3"], // Admin and John Doe
    patientJourneyReview: "Chemotherapy and bone marrow transplant consideration",
    learningPoints: "Infection prevention. Blood transfusion protocols.",
    status: "reviewing",
    createdAt: "2023-04-05T08:30:00Z",
    updatedAt: "2023-04-05T08:30:00Z",
    createdById: "9"
  },
  {
    id: "cd7",
    patientId: "patient7",
    diagnosisName: "Brain Tumor",
    leadStaffId: "6", // Dr. Sarah Johnson
    involvedStaffIds: ["3", "5"], // John Doe and Michael Johnson
    patientJourneyReview: "Surgery and radiation therapy planned",
    learningPoints: "Neurological assessment. Post-operative care.",
    status: "readyForMDT",
    createdAt: "2023-03-15T14:15:00Z",
    updatedAt: "2023-03-15T14:15:00Z",
    createdById: "6"
  },
  {
    id: "cd8",
    patientId: "patient1",
    diagnosisName: "Recurrent Breast Cancer",
    leadStaffId: "3", // John Doe
    involvedStaffIds: ["5", "7"], // Michael Johnson and Dr. James
    patientJourneyReview: "Re-evaluation and potential for stronger treatment",
    learningPoints: "Monitoring for recurrence. Follow-up protocols.",
    status: "reviewing",
    createdAt: "2023-05-20T11:45:00Z",
    updatedAt: "2023-05-20T11:45:00Z",
    createdById: "3"
  },
  {
    id: "cd9",
    patientId: "patient3",
    diagnosisName: "Metastatic Cancer",
    leadStaffId: "5", // Michael Johnson
    involvedStaffIds: ["4", "9"], // Jane Smith and Robert Chen
    patientJourneyReview: "Targeted therapy and palliative care considerations",
    learningPoints: "Metastasis monitoring. Pain management protocols.",
    status: "reviewing",
    createdAt: "2023-06-10T09:30:00Z",
    updatedAt: "2023-06-10T09:30:00Z",
    createdById: "5"
  }
];

// Mock data for patients
export const mockPatients = [
  {
    id: "patient1",
    name: "Alice Johnson",
    dateOfBirth: "1975-05-20",
    gender: "female",
    contactNumber: "07700 900000",
    address: "10 Downing Street, London",
    medicalHistory: "No significant medical history.",
    cancerDiagnosisId: "cd1"
  },
  {
    id: "patient2",
    name: "Bob Williams",
    dateOfBirth: "1982-08-12",
    gender: "male",
    contactNumber: "07700 900001",
    address: "221B Baker Street, London",
    medicalHistory: "Smoker for 20 years.",
    cancerDiagnosisId: "cd2"
  },
  {
    id: "patient3",
    name: "Charlie Brown",
    dateOfBirth: "1990-02-01",
    gender: "male",
    contactNumber: "07700 900002",
    address: "1 Infinite Loop, Cupertino",
    medicalHistory: "Family history of prostate cancer.",
    cancerDiagnosisId: "cd3"
  },
  {
    id: "patient4",
    name: "Diana Miller",
    dateOfBirth: "1968-11-03",
    gender: "female",
    contactNumber: "07700 900003",
    address: "1600 Pennsylvania Avenue, Washington D.C.",
    medicalHistory: "Frequent sun exposure.",
    cancerDiagnosisId: "cd4"
  },
  {
    id: "patient5",
    name: "Ethan Davis",
    dateOfBirth: "1979-06-15",
    gender: "male",
    contactNumber: "07700 900004",
    address: "P. Sherman 42 Wallaby Way, Sydney",
    medicalHistory: "History of polyps.",
    cancerDiagnosisId: "cd5"
  },
  {
    id: "patient6",
    name: "Fiona Green",
    dateOfBirth: "1985-09-22",
    gender: "female",
    contactNumber: "07700 900005",
    address: "Area 51, Nevada",
    medicalHistory: "Exposure to radiation.",
    cancerDiagnosisId: "cd6"
  },
  {
    id: "patient7",
    name: "George Harris",
    dateOfBirth: "1972-04-10",
    gender: "male",
    contactNumber: "07700 900006",
    address: "The Shire, Middle Earth",
    medicalHistory: "No known allergies.",
    cancerDiagnosisId: "cd7"
  }
];

// Export the Patient type for use in other files
export type { Patient };
